import {
  Button,
  Container,
  Form,
  FormField,
  Grid,
  Header,
  Input,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLSE } from '../../../store/lse.context';
import { LSEMessage } from '../../../types/Lse';
import { i18nKeys } from '../../../utils/i18n.utils';
import { fromPlainObject } from '../../../utils/mapper.utils';
import { LSE_ACTIONS } from './SectionModel';

const Settings: React.FC = () => {
  const emptyLseMessage = fromPlainObject({ text: '', url: undefined }, LSEMessage) as LSEMessage;
  const [bannerMessage, setBannerMessage] = useState('');
  const [learnMoreUrl, setLearnMoreUrl] = useState<string | undefined>();
  const [error, setError] = useState<string>('');
  const { currentLSE, createLSE, updateLSE, deactivateLSE, loading } = useLSE();
  const { t } = useTranslation();

  useEffect(() => {
    setBannerMessage(currentLSE?.text || '');
    setLearnMoreUrl(currentLSE?.url || undefined);
  }, [currentLSE]);

  const hasNoChanges = (): boolean => {
    const editedLSE = fromPlainObject({ text: bannerMessage, url: learnMoreUrl }, LSEMessage) as LSEMessage;
    // Polaris input resets this to empty string when LSEMessage.url can be undefined, this forces equality in that case
    if (editedLSE.url === '') {
      editedLSE.url = undefined;
    }
    return _.isEqual(editedLSE, currentLSE || emptyLseMessage);
  };

  const handleLearnMoreURL = (value: string) => {
    setLearnMoreUrl(value);
    if (isValidURL(value)) {
      setError('');
    } else {
      setError(t(i18nKeys.settings.lse.labels.invalidURL));
    }
  };

  const isValidURL = (str: string) => {
    try {
      const newUrl = new URL(str);
      return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch (err) {
      return false;
    }
  };

  const handleLSEAction = (action: LSE_ACTIONS) => {
    const editedLSE = fromPlainObject({ text: bannerMessage, url: learnMoreUrl }, LSEMessage) as LSEMessage;
    switch (action) {
      case LSE_ACTIONS.CREATE:
        createLSE(editedLSE);
        return;
      case LSE_ACTIONS.UPDATE:
        updateLSE(editedLSE);
        return;
      case LSE_ACTIONS.DISABLE:
        deactivateLSE();
        setBannerMessage('');
        setLearnMoreUrl('');
        return;
    }
  };

  return (
    <Container>
      <Grid gridDefinition={[{ colspan: 10 }]}>
        <Form
          variant="embedded"
          header={
            <Header variant="h2" description={t(i18nKeys.settings.lse.description)}>
              LSE
            </Header>
          }
          actions={
            <SpaceBetween direction="horizontal" size="s">
              {!currentLSE?.text && (
                <Button
                  className="button-right"
                  variant="primary"
                  disabled={Boolean(error) || hasNoChanges()}
                  loading={loading}
                  onClick={() => handleLSEAction(LSE_ACTIONS.CREATE)}>
                  {t(i18nKeys.settings.lse.buttons.create)}
                </Button>
              )}
              {currentLSE?.text && (
                <Button
                  className="button-right"
                  variant="normal"
                  onClick={() => handleLSEAction(LSE_ACTIONS.DISABLE)}
                  loading={loading}>
                  {t(i18nKeys.settings.lse.buttons.disable)}
                </Button>
              )}
              {currentLSE?.text && (
                <Button
                  className="button-right"
                  variant="primary"
                  disabled={hasNoChanges()}
                  onClick={() => handleLSEAction(LSE_ACTIONS.UPDATE)}
                  loading={loading}>
                  {t(i18nKeys.settings.lse.buttons.update)}
                </Button>
              )}
            </SpaceBetween>
          }>
          <SpaceBetween direction="vertical" size="s">
            <FormField label={t(i18nKeys.settings.lse.labels.bannerMessage)} stretch>
              <Textarea
                placeholder={t(i18nKeys.settings.lse.placeholder.bannerMessage)}
                onChange={({ detail }) => setBannerMessage(detail.value)}
                value={bannerMessage}
              />
            </FormField>
            <FormField label={t(i18nKeys.settings.lse.labels.learnMoreURL)} stretch errorText={learnMoreUrl && error}>
              <Input
                placeholder="https://health.aws.amazon.com"
                value={learnMoreUrl || ''}
                onChange={({ detail }) => handleLearnMoreURL(detail.value)}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Grid>
    </Container>
  );
};

export default Settings;
